import { MeterRateTaskType, RemoteResultMetaData } from '../../../redux/jobs/jobs.types';
import { RelBox } from '../../../api/ai.api.types';
import { RemoteResult, RemoteResultMeterRate } from '../../common/RemoteResultCommand/index.types';
import { revolveMeterTypeByObis, shortenObis } from '../../../helper/MeterHelper';
import { boxToCropSpace } from './overlayGeometry';
import { evidenceWeight, EVIDENCE_REQUIRED_MASS, EVIDENCE_STREAK_ACCEPT_COUNT, EVIDENCE_STREAK_MIN_CONFIDENCE } from './liveConstants';

export interface CollectedRegister {
    taskId: string;
    obis?: string;
    rateName?: string;
    value: string;
    confidence: number;
    imageBase64: string;
    // Display location RELATIVE TO imageBase64 — lets the confirm view crop the display snippet
    // out of the evidence image so the user can verify the value against the actual photo.
    displayBox?: RelBox;
    aiMeta?: RemoteResultMetaData;
    // Consensus state: how many independent responses reported exactly this value (same
    // normalized reading + same obis short form), and whether that reached the required count.
    // Manually edited values are accepted by definition (the user IS the confirmation).
    confirmations: number;
    accepted: boolean;
}

// One distinct (value, obis) reading seen for a task, with its best evidence so far. OCR noise
// produces the occasional wrong-but-confident reading; requiring the SAME value from multiple
// independent images before accepting filters exactly those random outliers.
interface RegisterCandidate {
    valueKey: string;
    obisKey: string;
    confirmations: number;
    // Confidence-weighted evidence collected for this value: each reading adds evidenceWeight(conf).
    // The value is accepted once mass >= EVIDENCE_REQUIRED_MASS. `confirmations` is kept as a plain
    // count of contributing readings (for display and the manual-edit guard).
    mass: number;
    // Consecutive identical readings of this value for its task; any other value read for the
    // same task resets it. Reaching EVIDENCE_STREAK_ACCEPT_COUNT accepts regardless of mass —
    // identical repetition is its own consensus signal (see liveConstants).
    streak: number;
    best: {
        value: string;
        confidence: number;
        imageBase64: string;
        displayBox?: RelBox;
        aiMeta?: RemoteResultMetaData;
    };
}

export interface AccumulatorState {
    expected: MeterRateTaskType[];
    collected: Record<string, CollectedRegister>;
    candidates: Record<string, RegisterCandidate[]>;
}

// Clamp a recognized reading to the register's configured post-decimal count. The AI may read
// more fractional digits than the tenant configures (e.g. a 3-decimal meter under a 1-decimal
// task); submitting those extra digits makes the backend reject the value (see issue #49) and
// overflows the confirm input (issue #52). Unlike normalizeReadingValue (the manual flow), this
// touches ONLY the fractional part — leading zeros and the integer part of a meter reading are
// significant and must survive verbatim.
export const truncateToPostDecimals = (value: string, postDecimals: number): string => {
    const dot = value.indexOf('.');
    if (dot === -1) {
        return value;
    }
    const intPart = value.slice(0, dot);
    if (postDecimals <= 0) {
        return intPart;
    }
    const fracPart = value.slice(dot + 1).slice(0, postDecimals);
    return fracPart.length ? `${intPart}.${fracPart}` : intPart;
};

// The AI sometimes reads a zero in front of the drum (e.g. "0123456" on a 6-digit meter); the
// confirm input is sized for exactly preDecimals digits and overflows (issue #52). Only zeros that
// do not fit are dropped — zeros within the configured digit count and every significant digit
// stay, so a genuinely too-long reading still surfaces through the range validation.
export const stripExcessLeadingZeros = (value: string, preDecimals: number | undefined): string => {
    if (!preDecimals || preDecimals <= 0) {
        return value;
    }
    const dot = value.indexOf('.');
    let intPart = dot === -1 ? value : value.slice(0, dot);
    const rest = dot === -1 ? '' : value.slice(dot);
    while (intPart.length > preDecimals && intPart.length > 1 && intPart.startsWith('0')) {
        intPart = intPart.slice(1);
    }
    return intPart + rest;
};

// More significant pre-decimal digits than the register allows (leading zeros do not count): the
// backend rejects such a value, and the manual input could not even hold it. Either the AI
// misread (e.g. a symbol next to the drum taken for a digit) or the tenant configured fewer digits
// than the meter has — in both cases the reading must not be taken over (qa#128).
export const exceedsPreDecimals = (value: string, preDecimals: number | undefined): boolean => {
    if (!preDecimals || preDecimals <= 0) {
        return false;
    }
    const stripped = stripExcessLeadingZeros(value, preDecimals);
    const dot = stripped.indexOf('.');
    return (dot === -1 ? stripped : stripped.slice(0, dot)).length > preDecimals;
};

export const initAccumulator =(meterRateTasks: MeterRateTaskType[]): AccumulatorState => ({
    expected: meterRateTasks ?? [],
    collected: {},
    candidates: {},
});

const findExpectedTask = (state: AccumulatorState, rate: RemoteResultMeterRate): MeterRateTaskType | undefined => {
    if (rate.obis) {
        // The AI usually returns the short obis notation ("1.8.0") while job tasks carry the
        // full form ("1-1:1.8.0") — compare both sides normalized, exactly like the single-shot
        // flow does in resolveRemoteResultOutcome.
        const rateObis = shortenObis(rate.obis);
        const byObis = state.expected.find((t) => t.obis && shortenObis(t.obis) === rateObis);
        if (byObis) {
            return byObis;
        }
    }
    if (rate.obisRateName) {
        const byRateName = state.expected.find((t) => t.rateName && t.rateName.toUpperCase() === rate.obisRateName?.toUpperCase());
        if (byRateName) {
            return byRateName;
        }
    }
    if (state.expected.length === 1) {
        const only = state.expected[0];
        if (!rate.obis && !rate.obisRateName) {
            return only;
        }
        // Do not assign a cross-medium misread (e.g. electricity OBIS on a gas meter);
        // format variants of the same medium remain allowed.
        if (rate.obis && only.obis && revolveMeterTypeByObis(rate.obis) !== revolveMeterTypeByObis(only.obis)) {
            return undefined;
        }
        if (rate.obisRateName && only.rateName && rate.obisRateName.toUpperCase() !== only.rateName.toUpperCase()) {
            return undefined;
        }
        return only;
    }
    return undefined;
};

// Numeric normalization so "039976.8" and "39976.8" confirm each other; non-numeric values fall
// back to a trimmed string compare.
const normalizeValueKey = (value: string): string => {
    const numeric = Number.parseFloat(value);
    return Number.isNaN(numeric) ? value.trim() : String(numeric);
};

// The winner among a task's candidates: accepted before pending, then most accumulated mass, then
// highest single confidence.
const isCandidateAccepted = (candidate: RegisterCandidate): boolean =>
    candidate.mass >= EVIDENCE_REQUIRED_MASS || candidate.streak >= EVIDENCE_STREAK_ACCEPT_COUNT;

const pickBestCandidate = (candidates: RegisterCandidate[]): RegisterCandidate | undefined =>
    [...candidates].sort((a, b) => {
        const aAccepted = isCandidateAccepted(a) ? 1 : 0;
        const bAccepted = isCandidateAccepted(b) ? 1 : 0;
        if (aAccepted !== bAccepted) return bAccepted - aAccepted;
        if (a.mass !== b.mass) return b.mass - a.mass;
        return b.best.confidence - a.best.confidence;
    })[0];

// `frameRegion` (viewport-relative region imageBase64 covers) must be passed when the result's
// boxes were mapped back to viewport coordinates (high-res crop reads) — the stored displayBox
// is always converted to be relative to imageBase64 itself.
export const accumulate = (state: AccumulatorState, result: RemoteResult, imageBase64: string, frameRegion?: RelBox): AccumulatorState => {
    let collected = state.collected;
    let candidates = state.candidates;

    for (const rate of result.meterRates ?? []) {
        if (!rate.readingValue) {
            continue;
        }
        // Below this the reading is pure noise (motion blur, half-covered display): it neither
        // contributes evidence nor counts toward a streak — and deliberately does not BREAK a
        // running streak either (a blurry frame between two sharp ones is not a contradiction).
        if (rate.readingValueConfidence <= EVIDENCE_STREAK_MIN_CONFIDENCE) {
            continue;
        }
        // Confidence-weighted evidence: a strong reading contributes ~1.0 (accepts alone), a weak
        // one only a fraction, and a reading at/below the floor nothing — but even a zero-weight
        // reading still counts toward the identical-repetition streak below.
        const weight = evidenceWeight(rate.readingValueConfidence);
        const target = findExpectedTask(state, rate);
        if (!target) {
            continue;
        }
        // Treated like noise: neither evidence nor a candidate, so it can never be pre-selected.
        if (exceedsPreDecimals(rate.readingValue, target.preDecimals)) {
            continue;
        }
        // A manually edited value is a user decision — recognition must not override it anymore.
        if (collected[target.taskId]?.accepted && collected[target.taskId].confirmations === 0) {
            continue;
        }

        const rawDisplayBox = rate.displayBox ?? result.detectedDisplayBox;
        const displayBox = rawDisplayBox && frameRegion ? boxToCropSpace(rawDisplayBox, frameRegion) : rawDisplayBox;
        const valueKey = normalizeValueKey(rate.readingValue);
        const obisKey = rate.obis ? shortenObis(rate.obis) : '';

        const previousCandidates = candidates[target.taskId] ?? [];
        const existingIndex = previousCandidates.findIndex((c) => c.valueKey === valueKey && c.obisKey === obisKey);
        // Streak bookkeeping: this reading extends its own candidate's run and resets every OTHER
        // candidate's run for the same task — "in a row" is judged per task, not per candidate.
        const taskCandidates = previousCandidates.map((candidate, index) => {
            if (index === existingIndex) {
                const better = rate.readingValueConfidence > candidate.best.confidence;
                return {
                    ...candidate,
                    confirmations: candidate.confirmations + 1,
                    mass: candidate.mass + weight,
                    streak: candidate.streak + 1,
                    best: better
                        ? { value: rate.readingValue as string, confidence: rate.readingValueConfidence, imageBase64, displayBox, aiMeta: result.meta }
                        : candidate.best,
                };
            }
            return candidate.streak !== 0 ? { ...candidate, streak: 0 } : candidate;
        });
        if (existingIndex < 0) {
            taskCandidates.push({
                valueKey,
                obisKey,
                confirmations: 1,
                mass: weight,
                streak: 1,
                best: { value: rate.readingValue, confidence: rate.readingValueConfidence, imageBase64, displayBox, aiMeta: result.meta },
            });
        }
        candidates = { ...candidates, [target.taskId]: taskCandidates };

        const winner = pickBestCandidate(taskCandidates);
        if (winner) {
            collected = {
                ...collected,
                [target.taskId]: {
                    taskId: target.taskId,
                    obis: target.obis,
                    rateName: target.rateName,
                    // Clamp to the register's configured digits so the confirm/submit value never
                    // carries more fractional digits than the backend accepts (#49) or more digits
                    // than the input field can show (#52). The candidate's raw value stays intact
                    // for consensus.
                    value: stripExcessLeadingZeros(truncateToPostDecimals(winner.best.value, target.postDecimals ?? 0), target.preDecimals),
                    confidence: winner.best.confidence,
                    imageBase64: winner.best.imageBase64,
                    displayBox: winner.best.displayBox,
                    aiMeta: winner.best.aiMeta,
                    confirmations: winner.confirmations,
                    accepted: isCandidateAccepted(winner),
                },
            };
        }
    }

    return collected === state.collected && candidates === state.candidates ? state : { ...state, collected, candidates };
};

// A register is only "done" once its value reached consensus; pending values keep the register
// on the missing list so the full-res read step gets a chance to confirm them.
export const missingRegisters = (state: AccumulatorState): MeterRateTaskType[] =>
    state.expected.filter((t) => !state.collected[t.taskId]?.accepted);

export const isComplete = (state: AccumulatorState): boolean => state.expected.length > 0 && missingRegisters(state).length === 0;
